import * as types from "../mutation-types";
import appConfig from "../../appconfig";
import {mapFeatureToggles} from "../../libraries/utils";
import engineFactory from "json-rules-engine";
import jwt_decode from "jwt-decode";

const rulesEngine = engineFactory([], { allowUndefinedFacts: true });

const generateRuleName = (params, getters) => {
	if(params.isProductsTest && Number.isInteger(getters.aBTestsShownItemsIndex)) {
		return `${params.name}_variant${(getters.aBTestsShownItemsIndex + 1)}`;
	}
	if(params.isProductCatalogsTest && Number.isInteger(getters.abTestShownCatalogIndex)) {
		return `${params.name}_variant${(getters.abTestShownCatalogIndex + 1)}`;
	}
	return params.name;
};

const handleProductAbTests = (ruleResult, userPct, commitFn) => {
	if(!ruleResult.event.params.products) {
		return;
	}
	const sortedProducts = ruleResult.event.params.products.value
		.sort((a, b) => {
			return a.percentage - b.percentage;
		});
	sortedProducts.forEach((product, index) => {
		product.percentage = product.percentage + (!sortedProducts[index - 1] ? 0 : sortedProducts[index - 1].percentage);
	});
	const indexToKeep = sortedProducts.findIndex((sp, i) => {
		return (sp.percentage >= userPct && sp.percentage <= sortedProducts[i].percentage);
	});
	if(indexToKeep > -1) {
		commitFn(types.SET_AB_TEST_SHOWN_ITEMS_INDEX, indexToKeep);
	}
	return indexToKeep > -1
		? sortedProducts.filter((_, i) => i !== indexToKeep).map(sp => sp.items).flat()
		: [];
};

const handleCatalogAbTests = (ruleResult, userPct, commitFn) => {
	if(!ruleResult.event.params.catalogs) {
		return;
	}
	const sortedCatalogs = ruleResult.event.params.catalogs.value
		.sort((a, b) => {
			return a.percentage - b.percentage;
		});
	sortedCatalogs.forEach((catalog, index) => {
		catalog.percentage = catalog.percentage + (!sortedCatalogs[index - 1] ? 0 : sortedCatalogs[index - 1].percentage);
	});
	const indexToKeep = sortedCatalogs.findIndex((sc, i) => {
		return (sc.percentage >= userPct && sc.percentage <= sortedCatalogs[i].percentage);
	});
	if(indexToKeep > -1) {
		commitFn(types.SET_AB_TEST_SHOWN_CATALOG_INDEX, indexToKeep);
	}
	return indexToKeep > -1
		? (sortedCatalogs.find((_, i) => i !== indexToKeep) || {}).extId
		: undefined;
};

const randomAssignment = () => {
	// legacy session refers to jwt session token that were previously generated on server side for ab testing.
	// the token was valid for 1 month, it should be safe to remove this legacy handler after 2 month of this commit.
	const abTestingSession = localStorage.getItem("abTestingSession");
	const legacySession = abTestingSession && jwt_decode(abTestingSession);
	if (legacySession && legacySession.pct) {
		localStorage.removeItem("abTestingSession");
		return legacySession.pct;
	}

	return Math.random() * 100;
};

const getVariants = () => {
	// Discard legacy data
	localStorage.removeItem("abTesting");
	let index = 0;
	const abTests = [];
	while(localStorage.getItem("abTesting" + index)) {
		abTests.push(localStorage.getItem("abTesting" + index));
		index++;
	}
	return new Set(abTests);
};

const updateVariants = (variants = []) => {
	const variantsArr = [...variants];
	if (!variantsArr || !variantsArr.length) {
		let index = 0;
		while(localStorage.getItem("abTesting" + index)) {
			localStorage.removeItem("abTesting" + index);
			index++;
		}
	}
	else {
		variantsArr.forEach((variant, index) => {
			localStorage.setItem("abTesting" + index, variant);
		});
	}
};

const updateKioskApplicationWithNewFTstate = (featureToggle) => {
	if (window.parent) {
		window.parent.postMessage({ type: "AB_TEST_FEATURE_TOGGLES_UPDATE", payload: featureToggle }, "*");
	}
};

// initial state
const state = {
	persistedData: {
		userPct: randomAssignment(),
		hiddenItems: [],
		shownItemsIndex: undefined,
		hiddenCatalogExtId: undefined,
		shownCatalogIndex: undefined
	}
};

// getters
const getters = {
	userPct: state => state.persistedData.userPct,
	aBTestsHiddenItems: state => state.persistedData.hiddenItems,
	aBTestsHiddenCatalogExtId: state => state.persistedData.hiddenCatalogExtId,
	aBTestsShownItemsIndex: state => state.persistedData.shownItemsIndex,
	abTestShownCatalogIndex: state => state.persistedData.shownCatalogIndex
};

// actions
const actions = {
	async startABTesting({ getters, rootGetters, commit, dispatch }, { tests, preview = false }) {
		updateVariants();

		if (!tests || !tests.length || !Array.isArray(tests)) {
			return;
		}
		tests.map(test => test.variants).flat().forEach(variant => rulesEngine.addRule(variant.rule));
		const addFacts = (fact) => dispatch("addABFacts", fact);
		// Stefan: not sure if this for shopId will work.... we had issues with kiosk,  getters.globalSelectedShop.id is not defined after order is completed on kiosk, hence I have added this other part with URL search params (but this will work only in kiosk - not sure if something else should be added here in case of non-kiosk cases...)
		let shopId;
		if (appConfig.channelAlias() === "kiosk") {
			const shopNumber = new URLSearchParams(window.location.search || new URL(localStorage.getItem("INITIAL_URL")).search).get("shopNumber");
			if (getters.globalSelectedShop) {
				shopId = getters.globalSelectedShop.id;
			} else {
				const shops = await dispatch("FETCH_AND_CACHE_SHOPS", {
					showSecretRestaurants: true
				});
				if (shops && shops.length) {
					shopId = shops.find(x => x.shopNumber === shopNumber).id;
				}
			}
		} else {
			if (getters.globalSelectedShop) {
				shopId = getters.globalSelectedShop.id;
			}
		}
		addFacts({ preview, percentage: getters.userPct, channel: appConfig.channelAlias(), shopId });

		rulesEngine.on("success", (event, _almanac, ruleResult) => {

			const isFeatureToggleTest = ruleResult.event.params && "featureToggles" in ruleResult.event.params;
			if (isFeatureToggleTest) {
				const { featureToggles } = event.params;
				const mappedFeatureToggles = mapFeatureToggles(featureToggles);
				const updatedFeatureToggles = {...getters.featureToggles, ...mappedFeatureToggles};
				commit(types.SET_AB_TEST_FEATURE_TOGGLES, updatedFeatureToggles);
				updateKioskApplicationWithNewFTstate(featureToggles);
			}

			const isProductsTest = ruleResult.event.params && "products" in ruleResult.event.params;
			if(isProductsTest) {
				commit(
					types.SET_AB_TEST_HIDDEN_ITEMS,
					Array.from(new Set(getters.aBTestsHiddenItems.concat(handleProductAbTests(ruleResult, getters.userPct, commit) || [])))
				);
			}

			const isProductCatalogsTest = ruleResult.event.params && "catalogs" in ruleResult.event.params;
			if(isProductCatalogsTest) {
				commit(types.SET_AB_TEST_HIDDEN_CATALOG, handleCatalogAbTests(ruleResult, getters.userPct, commit));
			}
			const ruleName = generateRuleName({
				name: ruleResult.name,
				isProductsTest,
				isProductCatalogsTest
			}, getters);

			const variants = getVariants();
			variants.delete(`${ruleResult.name}_control`);
			variants.add(ruleName);
			updateVariants(variants);

		});

		rulesEngine.on("failure", (_event, _almanac, ruleResult) => {

			const isProductsTest = ruleResult.event.params && "products" in ruleResult.event.params;
			const isFeatureToggleTest = ruleResult.event.params && "featureToggles" in ruleResult.event.params;
			const isProductCatalogsTest = ruleResult.event.params && "catalogs" in ruleResult.event.params;

			const shopsCondition = getters.globalSelectedShop && ruleResult
				.conditions
				.all
				.find(c => c.fact === "shopId");
			const channelCondition = ruleResult
				.conditions
				.all
				.find(c => c.fact === "channel");
			const isRuleApplicableToShop = !shopsCondition || shopsCondition.value.includes(getters.globalSelectedShop.id);
			const isRuleApplicableToChannel = !channelCondition || channelCondition.value.includes(appConfig.channelAlias());

			const ruleName = generateRuleName({
				name: ruleResult.name,
				isProductsTest,
				isProductCatalogsTest
			}, getters);

			if (isFeatureToggleTest) {
				const key = Object.keys(ruleResult.event.params.featureToggles)[0];
				const updated = {...getters.featureToggles, [key]: getters.defaultFeatureToggles[key] };
				commit(types.SET_AB_TEST_FEATURE_TOGGLES, updated);
			}

			if(isProductsTest) {
				commit(
					types.SET_AB_TEST_HIDDEN_ITEMS,
					Array.from(new Set(getters.aBTestsHiddenItems.concat(ruleResult.event.params.products.hiddenItems || [])))
				);
			}

			if(isProductCatalogsTest) {
				if(isRuleApplicableToShop) {
					commit(types.SET_AB_TEST_HIDDEN_CATALOG, ruleResult.event.params.catalogs.hiddenCatalog);
				} else {
					commit(types.SET_AB_TEST_HIDDEN_CATALOG);
					commit(types.SET_AB_TEST_SHOWN_CATALOG_INDEX);
				}
			}

			const variants = getVariants();
			variants.delete(ruleName);
			if(isRuleApplicableToShop && isRuleApplicableToChannel) {
				variants.add(`${ruleResult.name}_control`);
			}
			if(!getters.globalSelectedShop) {
				variants.delete(`${ruleResult.name}_control`);
			}
			updateVariants(variants);
		});

		this.watch(() => rootGetters.globalSelectedShop, (value) => addFacts({ shopId: (value || {}).id }));
		this.watch(() => rootGetters.customerEvaluationData, (value) => addFacts({ customerType: (value || {}).customerType }));
	},

	addABFacts(_, facts) {
		Object.entries(facts).forEach(([key, value]) => rulesEngine.addFact(key, value));
		rulesEngine.run().catch(error => console.warn("AB testing runner encountered an error", error)); // eslint-disable-line no-console
	}
};

// mutations
const mutations = {
	[types.SET_AB_TEST_HIDDEN_ITEMS](state, items) {
		state.persistedData.hiddenItems = items;
	},
	[types.SET_AB_TEST_HIDDEN_CATALOG](state, catalogExtId) {
		state.persistedData.hiddenCatalogExtId = catalogExtId;
	},
	[types.SET_AB_TEST_SHOWN_ITEMS_INDEX](state, index) {
		state.persistedData.shownItemsIndex = index;
	},
	[types.SET_AB_TEST_SHOWN_CATALOG_INDEX](state, index) {
		state.persistedData.shownCatalogIndex = index;
	}
};

export default {
	state,
	getters,
	actions,
	mutations
};
